import type { League, Match } from "@/types/match";

/**
 * API-Football tracks 1,200+ competitions — reserve teams, U19s, regional
 * lower-tier cups included — with zero built-in prioritization. On an
 * average weekday, those obscure leagues often have MORE fixtures than the
 * major ones, which is exactly what floods the Live and Schedule pages by
 * default. This module is what keeps the leagues people actually care
 * about front and center.
 *
 * Verified API-Football numeric league ids (as strings, matching how
 * apiFootball.ts maps `league.id`) for the competitions worth exact-id
 * matching — avoids false positives from name collisions (plenty of
 * countries have their own "Premier League"). Cross-checked against two
 * independent public references.
 */
const PRIORITY_API_FOOTBALL_IDS = new Set([
  "39", // Premier League (England)
  "140", // La Liga (Spain)
  "135", // Serie A (Italy)
  "78", // Bundesliga (Germany)
  "61", // Ligue 1 (France)
  "2", // UEFA Champions League
  "3", // UEFA Europa League
  "88", // Eredivisie (Netherlands)
  "94", // Primeira Liga (Portugal)
  "45", // FA Cup (England)
  "143", // Copa del Rey (Spain)
  "137", // Coppa Italia (Italy)
  "81", // DFB-Pokal (Germany)
  "66", // Coupe de France
]);

/** Same idea, for mock mode — src/data/leagues.ts's own catalog ids. */
const PRIORITY_CATALOG_IDS = new Set([
  "epl",
  "laliga",
  "seriea",
  "bundesliga",
  "ligue1",
  "ucl",
  "uel",
  "uecl",
  "mls",
  "brasileirao",
  "saudiproleague",
  "worldcup",
  "euros",
  "copaamerica",
  "afcon",
  "nationsleague",
  "clubworldcup",
  "libertadores",
  "facup",
  "copadelrey",
]);

// Fallback for competitions without a verified numeric id above — names
// distinctive enough in English that a substring match is safe (these
// don't collide with lower-tier/regional competition names).
const PRIORITY_NAME_PATTERNS = [
  /world cup/i,
  /european championship/i,
  /copa am[eé]rica/i,
  /africa(?:n)? cup of nations|afcon/i,
  /nations league/i,
  /club world cup/i,
  /libertadores/i,
  /^mls$|major league soccer/i,
  /brasileir[aã]o/i,
  /saudi pro league/i,
  /conference league/i,
  /europa league/i,
  /champions league/i,
];

export function isPriorityLeague(league: League): boolean {
  if (PRIORITY_API_FOOTBALL_IDS.has(league.id) || PRIORITY_CATALOG_IDS.has(league.id)) return true;
  return PRIORITY_NAME_PATTERNS.some((pattern) => pattern.test(league.name));
}

const rank = (league: League) => (isPriorityLeague(league) ? 0 : 1);

/** Priority leagues first; stable order preserved within each tier. */
export function sortMatchesByPriority(matches: Match[]): Match[] {
  return [...matches].sort((a, b) => rank(a.league) - rank(b.league));
}

/** Same, for a plain league list (e.g. filter pills). */
export function sortLeaguesByPriority(leagues: League[]): League[] {
  return [...leagues].sort((a, b) => rank(a) - rank(b));
}
